/* ============================================================
   CAMusic app page controller.

   Implementation of the logic in the CAMusic.dc.html design canvas. The
   canvas ran inside the Claude Design runtime (a React component whose
   handlers were bound with {{ }} placeholders); that syntax collides with
   Liquid, so handlers are bound here by data-act attribute instead, and
   selection state is carried on .is-on / .is-off classes rather than by
   writing inline styles from JS. Styles that ARE written inline are only
   the data-driven ones: album colour, gradients, transforms.

   Everything is scoped to the section root so the section survives being
   re-rendered by the theme editor.
   ============================================================ */

const AMB = {
  fireplace: { title: 'Fireplace', copy: 'A slow, uneven flicker in warm amber. The lamps nearest the floor carry most of it, so the light comes up from below the way a real fire does.' },
  thunderstorm: { title: 'Thunderstorm', copy: 'Long cold dark, then a strike, the whole room snaps to a hard blue-white for a moment, rolls off, and a second flash lands from a different corner.' },
  underwater: { title: 'Underwater', copy: 'Caustics. Light bends across the room in slow blue-green sheets, the far side first, and everything dims together as the swell passes over.' },
  aurora: { title: 'Aurora', copy: 'Slow curtains of green and violet drifting along the ceiling and up the tall lamps. The whole show lives in colour and movement.' },
  fireworks: { title: 'Fireworks', copy: 'Shells go up and open: one lamp cluster flares, the colour spreads outward through its neighbours, then falls away and leaves the room dark for the next one.' },
  train: { title: 'Light train', copy: 'A pale beam enters at the sofa and runs the length of the room, away towards the TV, and every lamp it passes keeps a warm glow for a moment after.' }
};

const AMB_HUE = { fireplace: 26, thunderstorm: 224, underwater: 192, aurora: 152, fireworks: 288, train: 212 };

const INT_COPY = {
  auto: 'Hands off. The app listens to the song and moves between these settings for you, gentle in the quiet parts, big when it lifts.',
  subtle: 'Soft and steady. The lights breathe with the music. For dinner, reading and having something on in the background.',
  medium: 'You can see the beat, in a gentle way. A relaxed everyday setting.',
  high: 'The room dances with the song: beats land on every lamp, and the colour changes as the track moves into a new part.',
  intense: 'Big and punchy. Hits arrive hard and fall away fast, and the lamps near the floor take the kick, a chorus takes the whole room at once.',
  extreme: 'A party. Light snaps on the beat and colours jump about. The hardest flashing of any level, with the flash limiter bypassed.'
};

/* what the app itself says under the Level pills (LightSyncRepository.MODE_BLURBS) */
const BLURB = {
  auto: 'Picks a level live from the music',
  subtle: 'Seamless: steady level, colour just flows',
  medium: 'Gentle club: visible dimming, soft flashes',
  high: 'The band: kicks, guitar and vocals split across lamps',
  intense: 'Club: the room follows energy, colour jumps on beats',
  extreme: 'Max club: dark room, fast beats chase side to side'
};

/* the show's status line, in the app's words (LightSyncScreen.showStatusText) */
const STATUS = { off: 'Lights are steady', waiting: 'Waiting for music on this phone', live: 'Reacting to the beat' };

const cap = (k) => k.charAt(0).toUpperCase() + k.slice(1);

/* ---------- the app's accent ----------
   The album's lead colour, lifted into the OKLCH lightness band the app
   holds its accents to (AlbumPalette.kt: 0.55 to 0.88), so it carries text
   and glows against true black. Hue is kept; chroma is pulled in only as far
   as sRGB needs. */
function hexRgb(hex) { const v = parseInt(String(hex).replace('#', ''), 16); return [(v >> 16) & 255, (v >> 8) & 255, v & 255]; }
function toLin(c) { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
function fromLin(c) { const v = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055; return Math.round(Math.max(0, Math.min(1, v)) * 255); }
function toOklab(rgb) {
  const r = toLin(rgb[0]), g = toLin(rgb[1]), b = toLin(rgb[2]);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const q = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * q, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * q, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * q];
}
function oklabLin(L, a, b) {
  const l = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3);
  const m = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3);
  const q = Math.pow(L - 0.0894841775 * a - 1.291485548 * b, 3);
  return [4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * q, -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * q, -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * q];
}
const linHex = (c) => '#' + c.map((v) => fromLin(v).toString(16).padStart(2, '0')).join('');
function liftAccent(hex) {
  const lab = toOklab(hexRgb(hex));
  const L = Math.max(0.55, Math.min(0.88, lab[0]));
  let k = 1;
  for (let i = 0; i < 30; i++) {
    const c = oklabLin(L, lab[1] * k, lab[2] * k);
    if (c.every((v) => v >= -0.0005 && v <= 1.0005)) return linHex(c);
    k *= 0.92;
  }
  return linHex(oklabLin(L, 0, 0));
}
function hslHex(h, s, l) {
  s /= 100; l /= 100;
  const k = (n) => (n + h / 30) % 12, a = s * Math.min(l, 1 - l);
  const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return '#' + [f(0), f(8), f(4)].map((v) => Math.round(v * 255).toString(16).padStart(2, '0')).join('');
}
/* black or white, whichever reads on a swatch (CoverPaletteEditor.contrastingInk) */
const needsLightInk = (hex) => { const c = hexRgb(hex); return (c[0] * 0.299 + c[1] * 0.587 + c[2] * 0.114) / 255 <= 0.55; };
const fmtTime = (s) => { s = Math.max(0, Math.floor(s || 0)); return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0'); };

/* ---------- the TV in the room shows CAMusic on Android TV ----------
   Drawn from the real Now Playing screen (camusic-shot-tv.png): the cover's
   light blurred across the screen, the cover, the title and artist in
   Roboto, the progress bar in the album's accent, the format chip, the play
   disc and the bottom navigation. The still part is painted once per
   record; the moving part a few times a second. */
class TvScreen {
  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = 640; this.canvas.height = 360;
    this.base = document.createElement('canvas');
    this.base.width = 640; this.base.height = 360;
    this.small = document.createElement('canvas');
    this.small.width = 20; this.small.height = 12;
    this.imgs = new Map();
    this.track = null; this.img = null; this.accent = '#D9A85C';
    this.t = 0; this.d = 0; this.playing = false;
    this.ready = document.fonts && document.fonts.load
      ? Promise.all([document.fonts.load('800 30px Roboto'), document.fonts.load('500 16px Roboto'), document.fonts.load('700 12px Roboto')]).catch(() => {})
      : Promise.resolve();
  }
  load(url) {
    if (!this.imgs.has(url)) {
      this.imgs.set(url, new Promise((res, rej) => { const im = new Image(); im.decoding = 'async'; im.onload = () => res(im); im.onerror = rej; im.src = url; }));
    }
    return this.imgs.get(url);
  }
  setTrack(track, accent) {
    this.track = track; this.accent = accent;
    Promise.all([this.load(track.cover), this.ready]).then((r) => {
      if (this.track !== track) return;
      this.img = r[0];
      this.paintBase();
      this.paint();
    }).catch(() => {});
  }
  paintBase() {
    const W = 640, H = 360, c = this.base.getContext('2d'), im = this.img, tr = this.track, A = this.accent;
    const s = this.small.getContext('2d');
    s.drawImage(im, 0, 0, this.small.width, this.small.height);
    c.imageSmoothingEnabled = true; c.imageSmoothingQuality = 'high';
    c.drawImage(this.small, -60, -60, W + 120, H + 120);
    let g = c.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, 'rgba(0,0,0,0.30)'); g.addColorStop(0.5, 'rgba(0,0,0,0.52)'); g.addColorStop(1, 'rgba(0,0,0,0.84)');
    c.fillStyle = g; c.fillRect(0, 0, W, H);
    /* the output chip, and the server this record came from */
    c.font = '600 12px Roboto, sans-serif'; c.textBaseline = 'middle'; c.textAlign = 'left';
    c.fillStyle = 'rgba(255,255,255,0.12)'; roundRect(c, 250, 14, 110, 26, 13); c.fill();
    c.fillStyle = A; roundRect(c, 346, 14, 44, 26, 13); c.fill();
    c.fillStyle = '#fff'; c.fillText('This phone', 272, 27.5);
    c.fillStyle = 'rgba(255,255,255,0.85)'; roundRect(c, 260, 21, 7, 12, 2); c.fill();
    c.fillStyle = '#000'; c.beginPath(); c.arc(368, 27, 6.5, 0, Math.PI * 2); c.fill();
    c.fillStyle = A; c.fillRect(367.2, 25.5, 1.6, 4.5); c.fillRect(367.2, 23.2, 1.6, 1.4);
    c.fillStyle = 'rgba(0,0,0,0.3)'; roundRect(c, 532, 14, 94, 26, 13); c.fill();
    c.strokeStyle = 'rgba(255,255,255,0.14)'; c.lineWidth = 1; c.stroke();
    c.fillStyle = '#4fd1c5'; c.beginPath(); c.arc(546, 27, 3.5, 0, Math.PI * 2); c.fill();
    c.fillStyle = 'rgba(255,255,255,0.85)'; c.fillText('Navidrome', 556, 27.5);
    /* the cover */
    const cs = 106, cx = W / 2 - cs / 2, cy = 52;
    c.save(); c.shadowColor = 'rgba(0,0,0,0.65)'; c.shadowBlur = 26; c.shadowOffsetY = 10;
    roundRect(c, cx, cy, cs, cs, 5); c.fillStyle = '#000'; c.fill(); c.restore();
    c.save(); roundRect(c, cx, cy, cs, cs, 5); c.clip(); c.drawImage(im, cx, cy, cs, cs); c.restore();
    /* title, artist */
    c.textAlign = 'center'; c.textBaseline = 'alphabetic';
    c.fillStyle = '#fff'; c.font = '800 30px Roboto, sans-serif';
    c.fillText(tr.label, W / 2, 196, W - 100);
    c.fillStyle = 'rgba(255,255,255,0.76)'; c.font = '500 17px Roboto, sans-serif';
    c.fillText(tr.artist, W / 2, 221, W - 100);
    /* the navigation along the foot of the screen */
    c.fillStyle = 'rgba(0,0,0,0.55)'; c.fillRect(0, H - 34, W, 34);
    c.fillStyle = 'rgba(255,255,255,0.08)'; c.fillRect(0, H - 34, W, 1);
    ['Playing', 'Library', 'Speakers', 'Lights', 'Settings'].forEach((t, i) => {
      const x = 64 + i * 128;
      c.fillStyle = i === 0 ? A : 'rgba(255,255,255,0.55)';
      c.beginPath(); c.arc(x, H - 22, 5, 0, Math.PI * 2); c.fill();
      c.font = '600 9px Roboto, sans-serif'; c.fillText(t, x, H - 7);
    });
  }
  paint() {
    if (!this.img) return;
    const W = 640, c = this.canvas.getContext('2d'), A = this.accent;
    c.drawImage(this.base, 0, 0);
    const x0 = 30, x1 = W - 30, y = 244, p = this.d ? Math.max(0, Math.min(1, this.t / this.d)) : 0, px = x0 + (x1 - x0) * p;
    c.fillStyle = 'rgba(255,255,255,0.2)'; roundRect(c, x0, y - 2, x1 - x0, 4, 2); c.fill();
    if (p > 0.001) { c.fillStyle = A; roundRect(c, x0, y - 2, px - x0, 4, 2); c.fill(); }
    c.fillStyle = '#fff'; c.beginPath(); c.arc(px, y, 6.5, 0, Math.PI * 2); c.fill();
    c.font = '500 12px Roboto, sans-serif'; c.fillStyle = 'rgba(255,255,255,0.7)'; c.textBaseline = 'middle';
    c.textAlign = 'left'; c.fillText(fmtTime(this.t), x0, y + 20);
    c.textAlign = 'right'; c.fillText(this.d ? fmtTime(this.d) : '--:--', x1, y + 20);
    /* the format chip */
    c.textAlign = 'center'; c.font = '600 11px "Roboto Mono", monospace';
    const chip = 'FLAC • 44.1/16 • 912 kb/s', cw = c.measureText(chip).width + 30;
    c.fillStyle = 'rgba(0,0,0,0.35)'; roundRect(c, W / 2 - cw / 2, y + 9, cw, 22, 11); c.fill();
    c.strokeStyle = A; c.globalAlpha = 0.55; c.lineWidth = 1; c.stroke(); c.globalAlpha = 1;
    c.fillStyle = A; c.beginPath(); c.arc(W / 2 - cw / 2 + 11, y + 20, 2.5, 0, Math.PI * 2); c.fill();
    c.fillStyle = '#fff'; c.fillText(chip, W / 2 + 5, y + 20.5);
    /* the transport */
    const ty = 296;
    c.save(); c.shadowColor = A; c.shadowBlur = 22;
    c.fillStyle = A; c.beginPath(); c.arc(W / 2, ty, 22, 0, Math.PI * 2); c.fill(); c.restore();
    c.fillStyle = '#000';
    if (this.playing) { c.fillRect(W / 2 - 7, ty - 8, 5, 16); c.fillRect(W / 2 + 2, ty - 8, 5, 16); }
    else { c.beginPath(); c.moveTo(W / 2 - 6, ty - 10); c.lineTo(W / 2 + 10, ty); c.lineTo(W / 2 - 6, ty + 10); c.closePath(); c.fill(); }
    c.fillStyle = 'rgba(255,255,255,0.9)';
    c.beginPath(); c.moveTo(W / 2 - 110, ty); c.lineTo(W / 2 - 98, ty - 8); c.lineTo(W / 2 - 98, ty + 8); c.closePath(); c.fill(); c.fillRect(W / 2 - 113, ty - 8, 2.5, 16);
    c.beginPath(); c.moveTo(W / 2 + 110, ty); c.lineTo(W / 2 + 98, ty - 8); c.lineTo(W / 2 + 98, ty + 8); c.closePath(); c.fill(); c.fillRect(W / 2 + 110.5, ty - 8, 2.5, 16);
  }
  tick(t, d, playing) {
    this.t = t; this.d = d; this.playing = playing;
    this.paint();
  }
}

const RUNG = [
  { copy: 'Everything on: equaliser, sound modes, volume levelling and Light Sync. The everyday mode.', off: [] },
  { copy: 'Carries more than 16 bits to the output where the decoder produced them. Equaliser, sound modes, volume levelling and Light Sync step aside while the float path is in use.', off: ['eq', 'mode', 'rg', 'ls'] },
  { copy: 'Experimental. CAMusic drives your USB DAC itself: the file’s own samples at the file’s own rate and bit depth, with the DAC’s hardware volume.', off: ['eq', 'mode', 'rg', 'ls', 'mix'] }
];

const MODE_COPY = {
  straight: 'The file as it was recorded.',
  vinyl: 'Crackle, pops and rumble that differ left from right, plus a soft surface hiss, the way a record sounds.',
  lofi: 'A gentle slowdown with tape-style wow and flutter: the pitch drifts a touch and the edges go soft.',
  radio: 'Squeezed to a telephone band with light saturation and AM static, as if the song is coming through an old set in the next room.'
};

const JOIN = {
  gapless: { copy: 'Butt-jointed: the last sample of one song meets the first sample of the next, a seamless join.', overlap: 0, offset: 0 },
  crossfade: { copy: 'A real overlap on any queue, from one to twelve seconds. Both songs play together, with their beats running side by side.', overlap: 0.30, offset: 0.42 },
  beat: { copy: 'With scanned tracks, CAMusic knows both beat grids before the join and slides the incoming song until the beats sit together. DJ Radio plans a whole set this way.', overlap: 0.30, offset: 0 }
};

/* ============================================================
   EXPANDED FEATURE DATA — libraries, streaming, driving, auto,
   mpd, multi-room, set builder, stats. Sourced from the repo
   README (feature parity-checked Sep 2026).
   ============================================================ */

const SERVERS = {
  navidrome: {
    name: 'Navidrome · Subsonic',
    copy: 'Browse, play, download and scrobble. Works with Navidrome and Subsonic-compatible servers such as Gonic, Airsonic, Ampache, Funkwhale and LMS. Continue listening picks up where you paused, and Hue Synco can pre-analyse the library.',
    caps: 'FULL SUPPORT · THE REFERENCE SERVER'
  },
  jellyfin: {
    name: 'Jellyfin',
    copy: 'Browse, play, download and scrobble, with Jellyfin\u2019s resume shelf in Continue listening. Artist biographies and album descriptions appear when your server has them.',
    caps: 'BROWSE · PLAY · DOWNLOAD · SCROBBLE'
  },
  emby: {
    name: 'Emby',
    copy: 'Browse, play, download and scrobble, with multi-disc albums and search across every category.',
    caps: 'BROWSE · PLAY · DOWNLOAD · SCROBBLE'
  },
  plex: {
    name: 'Plex',
    copy: 'Sign in through your browser at plex.tv, so your password stays with Plex. Browse, play, download and scrobble, with your server’s playback progress respected.',
    caps: 'BROWSE · PLAY · DOWNLOAD · SCROBBLE · SECURE SIGN-IN'
  },
  mpd: {
    name: 'MPD',
    copy: 'The server that plays its own music. Your phone is the remote over port 6600 for play, seek, queue, shuffle and repeat, and the sound goes straight from the server to your DAC. Works with moOde, Volumio, piCorePlayer and Mopidy.',
    caps: 'BROWSE · HARDWARE PLAYBACK · PROTOCOL REMOTE'
  },
  ma: {
    name: 'Music Assistant',
    copy: 'Playback across the whole house. CAMusic groups speakers, balances volumes, adjusts sync offsets and configures DSP, and this phone joins as a clock-synced Sendspin player. Discover rows follow your server’s providers.',
    caps: 'BROWSE · PLAY · GROUP · SCROBBLE'
  },
  local: {
    name: 'On-device files',
    copy: 'Turns phone storage into a library with the 10-band equaliser, sound modes, beat-matched transitions, the Set Builder and listening stats.',
    caps: 'BROWSE · PLAY · OFFLINE'
  },
  stream: {
    name: 'Spotify · Qobuz · Tidal',
    copy: 'Streaming accounts for listeners who add a subscription to their own library. Audio plays through CAMusic’s own engine with Light Sync, and all three are experimental while live-account testing finishes.',
    caps: 'EXPERIMENTAL · DIRECT STREAMING'
  },
  foobar: {
    name: 'foobar2000 (Beefweb)',
    copy: 'Control foobar2000 on your desktop through the Beefweb plugin, the way you control MPD: the desktop keeps the sound and your phone is the remote.',
    caps: 'BROWSE · PLAY · EXPERIMENTAL'
  }
};

const STREAMS = {
  spotify: {
    title: 'Spotify',
    html: '<ul><li><b>Embedded librespot client</b>, the same open-source Spotify client Music Assistant uses, decoding audio inside CAMusic so Light Sync reads the stream directly.</li><li>Browses saved tracks, albums, playlists and artist catalogues.</li><li>For Spotify Premium subscribers. Sign in with your Spotify credentials on your device.</li></ul>'
  },
  qobuz: {
    title: 'Qobuz',
    html: '<ul><li>Streams <b>studio-grade FLAC up to 24-bit / 192 kHz</b> into the playback engine, with full catalogue search and favourites.</li><li>Your favourites are your library: artists, albums, tracks and playlists.</li><li>Connects with your Qobuz subscription, and asks for app credentials alongside your login.</li></ul>'
  },
  tidal: {
    title: 'Tidal',
    html: '<ul><li>Signs in with a device code at link.tidal.com, so your password stays off the app.</li><li>Browses your favourites and playlists with full search.</li><li>Plays lossless FLAC and high-bitrate streams through Tidal playback manifests.</li></ul>'
  }
};

const SET_SHAPES = {
  warmup: { label: 'Warm-up', curve: [0.32, 0.38, 0.45, 0.55, 0.68, 0.82], copy: 'starts gentle and climbs steadily, leaving headroom for whatever comes after.' },
  peak: { label: 'Peak', curve: [0.78, 0.92, 0.95, 0.88, 0.86, 0.8], copy: 'held near the top: the scan\u2019s most energetic material first, close neighbours kept in reserve for the end.' },
  winddown: { label: 'Wind-down', curve: [0.82, 0.7, 0.55, 0.4, 0.3, 0.22], copy: 'comes down in steps, each join landing on the quieter side of the last.' },
  arc: { label: 'Arc', curve: [0.35, 0.55, 0.8, 0.95, 0.6, 0.35], copy: 'rises to a single peak and comes down the other side, shaping a complete set.' }
};

const STAT_COPY = {
  poster: '<b>The recap poster.</b> One shareable picture of your listening over 7 days, 30 days, 90 days or all time, drawn on your own phone from your own records, with charts of artist variety, streaks, lossless share and library sources.',
  clock: '<b>The listening clock.</b> Every play plotted by hour, showing the rhythm of your week across morning routines, focus hours and evening sessions, calculated on your device.',
  scatter: '<b>Tempo and energy, together.</b> Each dot is a track measured by the offline scan, with tempo across and energy up. The Set Builder and DJ Radio use these measurements to sequence sets.',
  keys: '<b>Keys and the BPM sweet spot.</b> Key detection correlates spectral peaks against harmonic templates, finding your dominant keys and BPM preferences to support smooth mixing.'
};

/* feature popup copy: shared by the sound-section tiles and the in-the-box grid.
   Copy is the app's behaviour, verified against the repo README. */
const FEAT_COPY = {
  eq: { t: 'Ten band equaliser', p: ['Ten bands plus a preamp for everything this phone plays, with automatic headroom so a boosted band stays clean on a loud master.', 'Music Assistant\u2019s own parametric DSP sits alongside it for the rooms it drives.'] },
  dac: { t: 'USB bit-perfect (experimental)', p: ['CAMusic drives your USB DAC with its own driver: the file\u2019s own samples at the file\u2019s own rate and bit depth, with the DAC\u2019s hardware volume.', 'The signal path page shows what the DAC itself confirmed, under Advanced settings.'] },
  gapless: { t: 'Gapless and beat matched', p: ['Gapless playback, a real crossfade from one to twelve seconds on any queue, and beat-matched joins on scanned tracks.', 'Smart fade, in DJ Radio, aligns on musical downbeats in whole bars.'] },
  multiri: { t: 'Clock-synced multi-room', p: ['As a Music Assistant speaker, the phone decodes FLAC, Opus and PCM into a native Oboe engine with its own timeline, kept in step by a Kalman filter.', 'A signed latency trim covers rooms that run ahead or behind, and the offset is saved for reconnects.'] },
  origq: { t: 'Original quality', p: ['Streams the original file from your library server whenever supported, preserving the exact bits you own.', 'Music Assistant offers a matching original quality option.'] },
  scan: { t: 'Your library, measured', p: ['An offline scan measures tempo, key, per-section energy and spectral balance from the records you own, in the background or on demand.', 'It powers beat matching, the Set Builder, harmonic mixing and Light Sync for speakers in other rooms.'] },
  djradio: { t: 'DJ Radio, one button', p: ['Pick an opener for Morning, Focus, Workout, Party, Sundown, Before sleep or Surprise me. Playback moves on by genre, energy, tempo and brightness, so the set keeps its flow.'] },
  setb: { t: 'Set Builder and harmonic mixing', p: ['Choose a shape such as warm-up, peak, wind-down or arc, and your tracks are ordered into that curve by key compatibility and tempo.', 'It reports scan coverage so you know what guides the set.'] },
  recap: { t: 'Your listening recap', p: ['A shareable recap poster made on your phone from your own listening history, over 7, 30 or 90 days, or all time.', 'It includes your listening clock, tempo and energy scatter, dominant keys, lossless share and top artists.'] },
  lyrics: { t: 'Synced lyrics', p: ['Synced lyrics from embedded tags, sidecar .lrc files or optionally LRCLIB, with tap to seek and a per-song timing nudge.', 'Online lookup is opt-in.'] },
  servers: { t: 'Every server', p: ['Navidrome and Subsonic servers, Jellyfin, Emby, Plex, MPD, Music Assistant, files on your device and experimental streaming accounts. Connect as many libraries as you own and switch freely.'] },
  downloads: { t: 'Downloads and scrobbling', p: ['Save albums for travel in a dedicated downloads view with storage caps and Wi-Fi-only options. Downloads resume.', 'Play counts and scrobbles go back to your server, and to ListenBrainz or Last.fm if you choose.'] },
  export: { t: 'Move to a new phone in one step', p: ['Servers, logins and settings export as one password-protected file (ten characters or more) and re-lock under the new phone\u2019s Keystore on import.'] },
  speaker: { t: 'Your phone becomes a speaker', p: ['CAMusic registers as a Sendspin player in Music Assistant, receiving streams and joining whole-house groups.', 'Announcements duck the music and restore it when they finish.'] },
  capture: { t: 'Other apps can drive the lights', p: ['An optional screen audio session lets games, films or other players feed the lighting engine.', 'It starts per session, under your direct control.'] },
  shows: { t: 'Save a show, tie it to a genre', p: ['Each show stores intensity, palette, brightness and dynamics as a preset.', 'Apply one instantly, bind it to a genre, or share it as a link.'] },
  airplay: { t: 'AirPlay casting', p: ['Send what this phone plays to an Apple TV, HomePod or other AirPlay speaker from the top left of Now Playing.'] },
  screens: { t: 'Phone, tablet, TV and car', p: ['Adaptive layouts for phones and tablets, D-pad navigation for Android TV, Android Auto and Android Automotive for the car, and a preview app for LG webOS.', 'On webOS the TV screen itself is the light show.'] }
};

/* twelve real records from the library, covers extracted from the
   files, scan numbers representative of what the offline scan measures.
   No audio plays anywhere on this page; these drive the Set Builder,
   DJ Radio and the recap demos. */
const LIB = [
  { t: 'Clocks', a: 'Coldplay', al: 'A Rush of Blood to the Head', bpm: 106, key: 'E♭ min', en: 0.78, cov: 'real-clocks' },
  { t: 'Chemical', a: 'Post Malone', al: 'AUSTIN', bpm: 92, key: 'F♯ min', en: 0.64, cov: 'real-chemical' },
  { t: 'Right as Rain', a: 'Adele', al: '19', bpm: 84, key: 'B maj', en: 0.48, cov: 'real-rightasarain' },
  { t: 'Friendly Creatures', a: 'Kupla', al: 'Imaginary', bpm: 78, key: 'A maj', en: 0.3, cov: 'real-friendly' },
  { t: 'Memory Palace', a: 'Mildlife', al: 'Automatic', bpm: 118, key: 'D min', en: 0.72, cov: 'real-memorypalace' },
  { t: 'The Other', a: 'Lauv', al: 'I met you when I was 18.', bpm: 100, key: 'B maj', en: 0.66, cov: 'real-theother' },
  { t: 'Love Hungry Man', a: 'AC/DC', al: 'Highway to Hell', bpm: 128, key: 'A maj', en: 0.9, cov: 'real-lovehungry' },
  { t: 'Don\u2019t Understand', a: 'Post Malone', al: 'AUSTIN', bpm: 88, key: 'G maj', en: 0.55, cov: 'real-chemical' },
  { t: 'Primrose', a: 'Kupla', al: 'Dimensions', bpm: 74, key: 'D maj', en: 0.24, cov: 'real-friendly' },
  { t: 'Marinating Sunsets', a: 'Lo-FI', al: 'Chill Beats to Study to', bpm: 82, key: 'C maj', en: 0.34, cov: 'real-friendly' },
  { t: 'American', a: 'Lana Del Rey', al: 'Born to Die', bpm: 96, key: 'C maj', en: 0.52, cov: 'real-rightasarain' },
  { t: 'Night.Moves', a: 'leaf beach', al: 'PostCards', bpm: 80, key: 'F maj', en: 0.28, cov: 'real-theother' }
];

/* demo set: the real library drives the Set Builder, DJ Radio and the recap */
const SET_TRACKS = LIB;

const AUTO_SHELVES = [
  { id: 'continue', name: 'Continue listening', kind: 'rows' },
  { id: 'recent', name: 'Recently added', kind: 'grid' },
  { id: 'artists', name: 'Artists', kind: 'people' },
  { id: 'lib', name: 'Navidrome, Albums A to Z', kind: 'rows' }
];

console.log('[camusic] controller build 2026-09-10T22:35 numeric-group');

export class CAMusicPage {
  constructor(root, config) {
    this.root = root;
    this.cfg = config || {};
    this.tracks = this.cfg.tracks || [];
    this.refs = {};
    this.listeners = [];

    this.mode = 'straight';
    this.modeAmt = { vinyl: 0.5, lofi: 0.5, radio: 0.5 };
    this.view = 'music';
    this.rung = 0;
    this.coverI = 0;
    this.trackI = 0;
    this.off = [];
    this.palMode = 'cover';
    this.joinMode = 'beat';
    this.joinScrub = null;
    this.flashCeil = 0.86;
    this.flashSlam = 0;
    this.flashHeld = 0;
    this.flashHist = [];
    this.ambKey = 'fireplace';
    this.ambVol = 0.55;
    /* expanded-feature demo state */
    this.auto = { grid: true, round: false, shelves: true, rwff: true };
    this.driveLimit = 80;
    this.driveTol = 4;
    this.driveV = 62;
    this.roomSet = {};
    this.roomOffset = 0;
    this.shape = 'peak';
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    /* the live demo */
    this.syncOn = true;
    this.playing = false;
    this.loading = false;
    this.started = false;
    this.muted = false;
    this.intensity = 'high';
    this.albumAccent = this.tracks.length ? liftAccent(this.tracks[0].sw[0]) : '#D9A85C';
    this.ambAccent = hslHex(26, 80, 62);
    this.weights = {};
    this.touch = window.matchMedia('(hover: none)').matches;
  }

  /* ---------- wiring ---------- */

  $(sel) { return this.root.querySelector(sel); }

  on(target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this.listeners.push([target, type, fn, opts]);
  }

  collectRefs() {
    this.root.querySelectorAll('[data-ref]').forEach((el) => {
      this.refs[el.getAttribute('data-ref')] = el;
    });
  }

  bindActions() {
    this.on(this.root, 'click', (e) => {
      const el = e.target.closest('[data-act]');
      if (!el || !this.root.contains(el)) return;
      const fn = this.actions[el.getAttribute('data-act')];
      if (fn) fn.call(this, el, e);
    });
    this.on(this.root, 'input', (e) => {
      const el = e.target.closest('[data-act-input]');
      if (!el || !this.root.contains(el)) return;
      const fn = this.actions[el.getAttribute('data-act-input')];
      if (fn) fn.call(this, el, e);
    });
    /* feature popup: Escape closes, focus stays out of the page behind */
    this.on(document, 'keydown', (e) => {
      if (e.key !== 'Escape' || !this.refs.featModal || !this.refs.featModal.classList.contains('is-open')) return;
      this.actions.closeFeat.call(this);
    });
  }

  importSpec(spec) {
    /* import() inside this module resolves relative to the MODULE's URL, but
       the config's paths are relative to the PAGE (Liquid gives absolute CDN
       URLs; the local harness gives "assets/x.js"). Resolve against the page. */
    if (/^(https?:)?\/\//.test(spec)) return import(spec);
    return import(new URL(spec, document.baseURI || location.href).href);
  }

  mount() {
    this.collectRefs();
    this.bindActions();
    this.applyLayout();
    this.on(window, 'resize', () => this.applyLayout());

    this.initDemoUi();
    this.importSpec(this.cfg.modules.engine).then((mod) => {
      if (!this.refs.canvas) return;
      this.demo = mod.createRoomDemo({
        canvas: this.refs.canvas,
        tip: this.refs.tip,
        tracks: this.tracks,
        onState: (s) => this.onDemoState(s)
      });
      this.tv = new TvScreen();
      this.demo.setTv(this.tv.canvas);
      this.setIntensity(INT_COPY[this.cfg.defaultIntensity] ? this.cfg.defaultIntensity : 'high');
      this.loadPalettes();
      this.ambRoom = mod.createAmbienceRoom({ canvas: this.refs.canvas, tip: this.refs.tip, show: 'fireplace', active: false });
    }).catch((err) => {
      if (this.refs.status) this.refs.status.textContent = 'The demo could not load';
      console.error('[camusic] engine failed to load', err);
    });

    this.startDemos();
    this.ambBleed('fireplace');
    this.initExtras();
  }

  destroy() {
    this.listeners.forEach(([t, ty, fn, o]) => t.removeEventListener(ty, fn, o));
    this.listeners = [];
    if (this.demo) this.demo.destroy();
    if (this.ambRoom) this.ambRoom.destroy();
    if (this.ambSound) this.ambSound.destroy();
    if (this.modePlayer) this.modePlayer.destroy();
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this._clockT) clearInterval(this._clockT);
    if (this._statusT) clearTimeout(this._statusT);
    if (this._mpdRaf) cancelAnimationFrame(this._mpdRaf);
    if (this._mpdBrT) clearInterval(this._mpdBrT);
  }

  /* ---------- handlers ---------- */

  get actions() {
    return CAMusicPage.ACTIONS;
  }

  /* ---------- selection helpers ---------- */

  /* one chip in a group is on; the rest are off */
  markGroup(host, attr, val) {
    if (!host) return;
    for (const b of host.children) {
      if (!b.hasAttribute(attr)) continue;
      b.classList.toggle('is-on', b.getAttribute(attr) === val);
    }
  }

  markIntensity(val) { this.markGroup(this.refs.intensity, 'data-int', val); }

  markAmbSound(on) {
    this.ambSoundOn = !!on;
    const host = this.refs.ambSound;
    if (host) for (const b of host.children) {
      const is = b.getAttribute('data-snd') === (on ? 'on' : 'off');
      b.classList.toggle('is-on', is);
      b.setAttribute('aria-checked', String(is));
    }
    if (this.refs.ambState) this.refs.ambState.textContent = on ? 'Running · with its sound' : 'Running · sound off';
  }

  /* in Effects the accent is the scene's own colour, as the app's is */
  ambBleed(key) {
    this.ambAccent = hslHex(AMB_HUE[key] == null ? 26 : AMB_HUE[key], 80, 64);
    if (this.view === 'ambience') this.setAccent(this.ambAccent);
  }

  /* ---------- the live demo: the phone and the room ---------- */

  initDemoUi() {
    const R = this.refs;
    /* the status bar keeps the visitor's own time */
    const fmt = new Intl.DateTimeFormat([], { hour: 'numeric', minute: '2-digit' });
    const clock = () => {
      if (!R.clock) return;
      /* hours and minutes only, as an Android status bar shows them */
      R.clock.textContent = fmt.formatToParts(new Date()).filter((p) => p.type === 'hour' || p.type === 'minute' || (p.type === 'literal' && p.value.trim() === ':')).map((p) => p.value).join('');
    };
    clock();
    this._clockT = setInterval(clock, 20000);
    if (R.hoverVerb && this.touch) R.hoverVerb.textContent = 'Tap any light';
    /* on a phone the room stays pinned under the site's menu bar while the
       app scrolls beneath it: the offset is the bar's own height */
    const pin = () => {
      const nav = document.querySelector('.cyborg-nav');
      if (R.camDemo) R.camDemo.style.setProperty('--cam-pin', (nav ? Math.round(nav.getBoundingClientRect().height) : 0) + 'px');
    };
    pin();
    this.on(window, 'resize', pin);
    this.root.querySelectorAll('.cam-range').forEach((el) => this.paintRange(el));
    this.renderStatus();
    this.renderPlay();
  }

  paintRange(el) {
    const min = +el.min || 0, max = +el.max || 100;
    el.style.setProperty('--v', ((+el.value - min) / (max - min) * 100).toFixed(1) + '%');
  }

  setAccent(hex) {
    if (!hex || !this.refs.camDemo) return;
    this.refs.camDemo.style.setProperty('--cam-accent', hex);
  }

  /* the album's cover washes the Light Sync page, crossfading between records */
  setWash(url) {
    const host = this.refs.wash;
    if (!host || !url || this._washUrl === url) return;
    this._washUrl = url;
    if (!this._wash) {
      this._wash = [document.createElement('i'), document.createElement('i')];
      this._wash.forEach((l) => host.appendChild(l));
      this._washI = 0;
    }
    const next = this._wash[this._washI ^= 1], prev = this._wash[this._washI ^ 1];
    const im = new Image();
    im.onload = () => {
      if (this._washUrl !== url) return;
      next.style.backgroundImage = 'url("' + url + '")';
      next.classList.add('is-on');
      prev.classList.remove('is-on');
    };
    im.src = url;
  }

  setIntensity(k) {
    this.intensity = k;
    this._level = null;
    const host = this.refs.intensity;
    if (host) for (const b of host.children) {
      const on = b.getAttribute('data-int') === k;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-checked', String(on));
    }
    if (host) {
      /* the chosen level is always in view, as the app scrolls it */
      const on = host.querySelector('.is-on');
      if (on) {
        const x = on.getBoundingClientRect().left - host.getBoundingClientRect().left + host.scrollLeft;
        const to = Math.max(0, x - (host.clientWidth - on.offsetWidth) / 2);
        if (host.scrollTo) host.scrollTo({ left: to, behavior: this._rungsSeen ? 'smooth' : 'auto' }); else host.scrollLeft = to;
        this._rungsSeen = true;
      }
    }
    if (this.demo) this.demo.setIntensity(k);
    if (this.refs.levelName) this.refs.levelName.textContent = cap(k);
    if (this.refs.blurb) this.refs.blurb.textContent = BLURB[k] || '';
    this.renderCaption();
  }

  renderCaption() {
    const c = this.refs.intCopy;
    if (!c) return;
    if (this.view === 'ambience') {
      const a = AMB[this.ambKey] || AMB.fireplace;
      c.innerHTML = '<b>' + a.title + ':</b> ' + a.copy;
    } else {
      c.innerHTML = '<b>' + cap(this.intensity) + ':</b> ' + (INT_COPY[this.intensity] || '');
    }
  }

  renderStatus() {
    const R = this.refs, live = this.syncOn && this.playing;
    if (R.status && !this._statusHold) R.status.textContent = !this.syncOn ? STATUS.off : this.playing ? STATUS.live : STATUS.waiting;
    if (R.livePill) R.livePill.classList.toggle('is-live', live);
    if (R.liveText) R.liveText.textContent = live ? 'Live' : 'Off';
    if (R.syncCard) { R.syncCard.classList.toggle('is-on', this.syncOn); R.syncCard.classList.toggle('is-live', live); }
    if (R.musicPanel) R.musicPanel.classList.toggle('is-idle', !live);
  }

  /* an error holds the status line for a moment, then the show's own words return */
  flashStatus(msg) {
    if (!this.refs.status || !msg) return;
    this.refs.status.textContent = msg;
    this._statusHold = true;
    if (this._statusT) clearTimeout(this._statusT);
    this._statusT = setTimeout(() => { this._statusHold = false; this.renderStatus(); }, 4200);
  }

  renderPlay() {
    const R = this.refs, tr = this.tracks[this.trackI || 0];
    if (R.miniPlay) {
      R.miniPlay.classList.toggle('is-playing', this.playing);
      R.miniPlay.classList.toggle('is-loading', this.loading);
      R.miniPlay.setAttribute('aria-label', (this.playing ? 'Pause ' : 'Play ') + (tr ? tr.label : ''));
    }
    if (R.cue) R.cue.classList.toggle('is-gone', this.started || this.loading || this.view === 'ambience');
    if (R.camDemo) R.camDemo.classList.toggle('is-audible', this.started);
    if (R.tracks) for (const b of R.tracks.children) b.classList.toggle('is-playing', this.playing && b.classList.contains('is-on'));
    this.renderStatus();
  }

  /* a few times a second: the mini player's progress and the TV's picture */
  tickDemo() {
    const now = performance.now();
    if (this._tickAt && now - this._tickAt < 240) return;
    this._tickAt = now;
    const m = this.demo && this.demo.media();
    const t = m ? m.t : 0, d = m && m.d ? m.d : 0;
    if (this.refs.miniProg) this.refs.miniProg.style.width = (d ? t / d * 100 : 0).toFixed(2) + '%';
    if (this.tv) this.tv.tick(t, d, this.playing);
  }

  selectTrack(i) {
    const n = this.tracks.length;
    i = ((i % n) + n) % n;
    this.applyAlbum(i);
    if (this.demo) this.demo.select(i);
    this.renderPlay();
  }

  /* which preset the current bypass set matches, or Custom */
  applyChain(forced) {
    let r = forced;
    if (r == null) {
      r = -1;
      for (let i = 0; i < RUNG.length; i++) {
        const o = RUNG[i].off;
        if (o.length === this.off.length && o.every((k) => this.off.indexOf(k) >= 0)) { r = i; break; }
      }
    }
    this.rung = r;
    this.markGroup(this.refs.rungs, 'data-r', r < 0 ? 'none' : String(r));
    if (this.refs.rungCopy) {
      this.refs.rungCopy.textContent = r < 0
        ? 'Your own chain: ' + (this.off.length ? this.off.length + ' stage' + (this.off.length > 1 ? 's' : '') + ' bypassed' : 'nothing bypassed') + '. Every stage in the app can be stepped over on its own.'
        : RUNG[r].copy;
    }
    if (this.refs.chain) {
      for (const el of this.refs.chain.children) {
        const c = el.getAttribute('data-c');
        if (!c) continue;
        el.classList.toggle('is-off', this.off.indexOf(c) >= 0);
      }
    }
  }

  /* the demo is two panels wide when there is room for both, stacked when not,
     so the Light Sync panel never gets squeezed into a three-line title */
  applyLayout() { /* the demo lays itself out by its own width: container queries in the CSS */ }

  onDemoState(s) {
    let changed = false;
    if (s.loading !== undefined && !!s.loading !== this.loading) { this.loading = !!s.loading; changed = true; }
    if (s.playing !== undefined && !!s.playing !== this.playing) {
      this.playing = !!s.playing;
      if (this.playing) this.started = true;
      changed = true;
    }
    if (changed) this.renderPlay();
    if (s.error) this.flashStatus(s.status);
    if (this.intensity === 'auto' && s.level && s.level !== this._level) {
      this._level = s.level;
      if (this.refs.levelName) this.refs.levelName.textContent = 'Auto · ' + s.level;
      if (this.refs.blurb) this.refs.blurb.innerHTML = BLURB.auto + '. <b>On ' + s.level + ' now</b>';
    }
    if (s.tick) this.tickDemo();
  }

  /* ---------- live demo panels ---------- */

  startDemos() {
    this.fit(this.refs.modeCanvas);
    this.fit(this.refs.joinCanvas);
    this.fit(this.refs.flashCanvas);
    this.flashHist = [];
    this.selectCover(0);
    this.startTilt();
    this.wireJoin();
    this.wireFlash();
    this.applyChain(0);
    this.syncModeAmount();

    const frame = (now) => {
      const t = now / 1000;
      this.drawModes(t);
      this.drawJoins(t);
      this.drawFlash(t);
      this.raf = requestAnimationFrame(frame);
    };

    /* reduced motion still gets the panels drawn, just not animated. The
       design canvas returned early here and left these three cards blank. */
    if (this.reduced) {
      this.drawModes(0);
      this.drawJoins(0);
      this.drawFlash(0);
    } else {
      this.raf = requestAnimationFrame(frame);
    }

    this.on(window, 'resize', () => {
      this.fit(this.refs.modeCanvas);
      this.fit(this.refs.joinCanvas);
      this.fit(this.refs.flashCanvas);
      if (this.reduced) { this.drawModes(0); this.drawJoins(0); this.drawFlash(0); }
    });
  }

  wireJoin() {
    const c = this.refs.joinCanvas;
    if (!c) return;
    let drag = false;
    const move = (ev) => {
      const r = c.getBoundingClientRect();
      this.joinScrub = Math.max(0, Math.min(1, (ev.clientX - r.left - 14) / Math.max(1, r.width - 28)));
      if (this.reduced) this.drawJoins(0);
    };
    this.on(c, 'pointerdown', (ev) => { drag = true; c.setPointerCapture(ev.pointerId); move(ev); });
    this.on(c, 'pointermove', (ev) => { if (drag) move(ev); });
    this.on(c, 'pointerup', () => { drag = false; setTimeout(() => { if (!drag) this.joinScrub = null; }, 2200); });
  }

  wireFlash() {
    const c = this.refs.flashCanvas;
    if (!c) return;
    let drag = false;
    const move = (ev) => {
      const r = c.getBoundingClientRect();
      const h = r.height;
      const yy = ev.clientY - r.top;
      const v = (h - 14 - yy) / Math.max(1, (h - 30) * 0.52);
      this.flashCeil = Math.max(0.25, Math.min(1.4, v));
      if (this.reduced) this.drawFlash(0);
    };
    this.on(c, 'pointerdown', (ev) => { drag = true; c.setPointerCapture(ev.pointerId); move(ev); });
    this.on(c, 'pointermove', (ev) => { if (drag) move(ev); });
    this.on(c, 'pointerup', () => { drag = false; });
  }

  fit(c) {
    if (!c) return null;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const r = c.getBoundingClientRect();
    if (!r.width) return null;
    c.width = Math.round(r.width * dpr);
    c.height = Math.round(r.height * dpr);
    const ctx = c.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { ctx: ctx, w: r.width, h: r.height };
  }

  ctxOf(c) {
    if (!c || !c.width) return null;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const ctx = c.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { ctx: ctx, w: c.width / dpr, h: c.height / dpr };
  }

  syncModeAmount() {
    const m = this.mode, has = this.modeAmt[m] !== undefined;
    if (this.refs.modeAmtRow) this.refs.modeAmtRow.classList.toggle('is-shown', has);
    if (!has) return;
    const v = this.modeAmt[m];
    const name = m === 'lofi' ? 'Lo-fi' : m === 'radio' ? 'Old radio' : 'Vinyl';
    if (this.refs.modeAmtLabel) this.refs.modeAmtLabel.textContent = name + ' amount';
    if (this.refs.modeAmt) this.refs.modeAmt.value = String(Math.round(v * 100));
    if (this.refs.modeAmtVal) this.refs.modeAmtVal.textContent = Math.round(v * 100) + '%';
  }

  drawModes(t) {
    const g = this.ctxOf(this.refs.modeCanvas);
    if (!g) return;
    const ctx = g.ctx, w = g.w, h = g.h, mid = h / 2;
    ctx.clearRect(0, 0, w, h);
    ctx.strokeStyle = 'rgba(255,255,255,0.07)';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, mid); ctx.lineTo(w, mid); ctx.stroke();
    const m = this.mode;
    if (this.modePlayer && this.modePlayer.playing) {
      const N = this.modePlayer.size;
      if (!this._wbuf || this._wbuf.length !== N) this._wbuf = new Uint8Array(N);
      if (this.modePlayer.read(this._wbuf)) {
        ctx.beginPath();
        for (let x = 0; x <= w; x++) {
          const v = (this._wbuf[Math.floor((x / w) * (N - 1))] - 128) / 128;
          const y = mid - v * h * 0.44;
          x ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
        }
        ctx.strokeStyle = '#E4A667'; ctx.lineWidth = 1.6; ctx.stroke();
        ctx.fillStyle = 'rgba(255,255,255,0.45)';
        ctx.font = '11px Inter, system-ui, sans-serif';
        ctx.fillText('live signal · ' + m, 8, 15);
        return;
      }
    }
    const A = this.modeAmt[m] !== undefined ? this.modeAmt[m] : 0.5;
    const wob = m === 'lofi' ? 1 + 0.22 * A * Math.sin(t * 1.7) : 1;
    ctx.beginPath();
    for (let x = 0; x <= w; x++) {
      const p = (x / w) * 10 * wob + t * 2.4;
      let v = Math.sin(p) * 0.52 + Math.sin(p * 2.1 + 0.6) * 0.2;
      if (m === 'straight' || m === 'vinyl') v += Math.sin(p * 5.3) * 0.14 + Math.sin(p * 9.7) * 0.07;
      if (m === 'vinyl') v *= 1 - 0.12 * A;
      if (m === 'lofi') v = v * (1 - 0.2 * A) + Math.sin(p * 3.1) * 0.12 * A;
      if (m === 'radio') { const q = 1 - 0.55 * A; v = (Math.sin(p) * 0.52 + Math.sin(p * 1.6) * 0.16) * q; const lim = 0.55 - 0.24 * A; v = Math.max(-lim, Math.min(lim, v)); }
      const y = mid - v * (h * 0.40);
      x ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
    }
    ctx.strokeStyle = '#E4A667';
    ctx.lineWidth = 1.8;
    ctx.stroke();
    if (m === 'vinyl') {
      ctx.fillStyle = 'rgba(255,255,255,' + (0.12 + 0.5 * A).toFixed(2) + ')';
      for (let i = 0; i < Math.round(4 + 34 * A); i++) {
        const s = Math.sin(i * 12.9898 + Math.floor(t * 8) * 78.233) * 43758.5453;
        const rx = (s - Math.floor(s));
        const s2 = Math.sin(i * 4.898 + Math.floor(t * 8) * 39.31) * 20443.1;
        const ry = (s2 - Math.floor(s2));
        ctx.fillRect(rx * w, mid + (ry - 0.5) * h * 0.7, 1.2, 2 + ry * 3);
      }
    }
    if (m === 'radio') {
      ctx.strokeStyle = 'rgba(255,255,255,0.14)';
      ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(0, mid - h * 0.14); ctx.lineTo(w, mid - h * 0.14);
      ctx.moveTo(0, mid + h * 0.14); ctx.lineTo(w, mid + h * 0.14); ctx.stroke();
      ctx.setLineDash([]);
    }
  }

  drawJoins(t) {
    const g = this.ctxOf(this.refs.joinCanvas);
    if (!g) return;
    const ctx = g.ctx, w = g.w, h = g.h;
    ctx.clearRect(0, 0, w, h);
    const spec = JOIN[this.joinMode];
    const pad = 14, barH = 26, gapY = 18;
    const yA = h / 2 - barH - gapY / 2, yB = h / 2 + gapY / 2;
    const beat = 26;
    const cycle = 7;
    const ph = this.joinScrub != null ? this.joinScrub : (t % cycle) / cycle;
    const span = w - pad * 2;
    const aEnd = pad + span * (0.5 + spec.overlap / 2);
    const bStart = pad + span * (0.5 - spec.overlap / 2);
    const drawBar = (y, x0, x1, label, hot, off) => {
      ctx.fillStyle = hot ? 'rgba(228,166,103,0.16)' : 'rgba(255,255,255,0.05)';
      ctx.strokeStyle = hot ? 'rgba(228,166,103,0.55)' : 'rgba(255,255,255,0.14)';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.rect(x0, y, x1 - x0, barH); ctx.fill(); ctx.stroke();
      ctx.strokeStyle = hot ? 'rgba(228,166,103,0.75)' : 'rgba(255,255,255,0.28)';
      for (let x = pad + 6 + off * beat; x < x1 - 2; x += beat) {
        if (x < x0) continue;
        ctx.beginPath(); ctx.moveTo(x, y + 5); ctx.lineTo(x, y + barH - 5); ctx.stroke();
      }
      ctx.fillStyle = hot ? '#E4A667' : 'rgba(255,255,255,0.55)';
      ctx.font = '11px Inter, system-ui, sans-serif';
      ctx.fillText(label, x0 + 4, y - 6);
    };
    drawBar(yA, pad, aEnd, 'Track A', false, 0);
    drawBar(yB, bStart, w - pad, 'Track B', true, spec.offset);
    if (spec.overlap > 0) {
      ctx.fillStyle = 'rgba(228,166,103,0.10)';
      ctx.fillRect(bStart, yA - 2, aEnd - bStart, (yB + barH) - yA + 4);
      ctx.strokeStyle = 'rgba(228,166,103,0.35)';
      ctx.setLineDash([3, 4]);
      ctx.strokeRect(bStart, yA - 2, aEnd - bStart, (yB + barH) - yA + 4);
      ctx.setLineDash([]);
    } else {
      ctx.strokeStyle = 'rgba(228,166,103,0.6)';
      ctx.beginPath(); ctx.moveTo(aEnd, yA - 6); ctx.lineTo(aEnd, yB + barH + 6); ctx.stroke();
    }
    ctx.font = '11px Inter, system-ui, sans-serif';
    const inJoin = ph * span + pad >= bStart && ph * span + pad <= aEnd;
    ctx.fillStyle = this.joinMode === 'crossfade' ? 'rgba(255,140,120,0.85)' : 'rgba(255,255,255,0.6)';
    ctx.fillText(this.joinMode === 'gapless' ? 'no gap, no fade'
      : this.joinMode === 'crossfade' ? 'beats collide here' : 'beats line up here',
      Math.min(w - 130, bStart + 6), h - 6);
    const px = pad + ph * span;
    ctx.strokeStyle = inJoin ? '#E4A667' : '#F0BE8A';
    ctx.lineWidth = 1.6;
    ctx.beginPath(); ctx.moveTo(px, 8); ctx.lineTo(px, h - 20); ctx.stroke();
    ctx.fillStyle = inJoin ? '#E4A667' : '#F0BE8A';
    ctx.beginPath(); ctx.arc(px, 8, 3.5, 0, 6.2832); ctx.fill();
  }

  drawFlash(t) {
    const g = this.ctxOf(this.refs.flashCanvas);
    if (!g) return;
    const ctx = g.ctx, w = g.w, h = g.h;
    ctx.clearRect(0, 0, w, h);
    if (!this.flashHist) this.flashHist = [];
    this.flashSlam = Math.max(0, this.flashSlam - 0.012);
    const req = Math.max(0, Math.sin(t * 3.1) * 0.6 + Math.sin(t * 7.3) * 0.5 + 0.35)
      + this.flashSlam * (0.5 + 0.5 * Math.abs(Math.sin(t * 9.4)));
    this.flashHist.push(req);
    const N = Math.round(w / 2);
    while (this.flashHist.length > N) this.flashHist.shift();
    const ceil = this.flashCeil;
    const y = (v) => h - 14 - Math.min(1.6, v) * (h - 30) * 0.52;
    ctx.strokeStyle = 'rgba(255,255,255,0.28)';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    this.flashHist.forEach((v, i) => { const x = i * 2; i ? ctx.lineTo(x, y(v)) : ctx.moveTo(x, y(v)); });
    ctx.stroke();
    ctx.strokeStyle = '#E4A667';
    ctx.lineWidth = 2;
    ctx.beginPath();
    let sm = 0, held = 0;
    this.flashHist.forEach((v, i) => {
      if (v > ceil) held++;
      const capped = Math.min(v, ceil);
      sm += (capped - sm) * 0.22;
      const x = i * 2;
      i ? ctx.lineTo(x, y(sm)) : ctx.moveTo(x, y(sm));
    });
    ctx.stroke();
    ctx.strokeStyle = 'rgba(228,166,103,0.55)';
    ctx.setLineDash([5, 5]);
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, y(ceil)); ctx.lineTo(w, y(ceil)); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(228,166,103,0.85)';
    ctx.font = '11px Inter, system-ui, sans-serif';
    ctx.fillText('brightness ceiling · ' + Math.round(ceil * 100) + '%', 8, y(ceil) - 6);
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.fillText('what the music asks for', 8, 16);
    if (this.refs.flashRead && Math.abs(held - this.flashHeld) > 2) {
      this.flashHeld = held;
      const pct = Math.round(held / Math.max(1, this.flashHist.length) * 100);
      this.refs.flashRead.textContent = pct + '% of the last few seconds was pulled back before it reached the lamps.';
    }
  }

  /* ---------- album colour ----------
     every album keeps two or three saved colours; the library bars, the cover
     card, the panel bleed and the room all read this one list */

  loadPalettes() {
    this.tracks.forEach((tr, i) => { tr.pal = tr.sw; this.paintTrackBars(i, tr.sw); });
    this.applyAlbum(this.trackI || 0);
    this.selectCover(this.coverI);
  }

  palOf(i) { const tr = this.tracks[i]; return (tr && (tr.pal || tr.sw)) || []; }

  /* one record takes over: the phone, the TV, the room and the records row */
  applyAlbum(i) {
    this.trackI = i;
    const R = this.refs, tr = this.tracks[i], cols = this.palOf(i);
    this.albumAccent = liftAccent(cols[0]);
    if (this.view !== 'ambience') this.setAccent(this.albumAccent);
    if (R.camDemo) {
      R.camDemo.style.setProperty('--cam-sw0', cols[0]);
      R.camDemo.style.setProperty('--cam-sw1', cols[1] || cols[0]);
    }
    this.setWash(tr.cover);
    if (R.palAlbum) R.palAlbum.textContent = tr.label + ' · ' + tr.artist;
    if (R.miniCover) { R.miniCover.src = tr.cover; R.miniCover.alt = tr.label + ' cover'; }
    if (R.miniTitle) R.miniTitle.textContent = tr.label;
    if (R.miniArtist) R.miniArtist.textContent = tr.artist;
    if (R.cueTitle) R.cueTitle.textContent = 'Play “' + tr.label + '”';
    if (R.tracks) for (const b of R.tracks.children) {
      const on = +b.getAttribute('data-i') === i;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', String(on));
    }
    if (this.tv) this.tv.setTrack(tr, this.albumAccent);
    if (this.demo) {
      /* the TV's picture is mostly the cover's own colours, dimmed */
      const rgb = cols.map(hexRgb).reduce((a, c) => [a[0] + c[0], a[1] + c[1], a[2] + c[2]], [0, 0, 0]).map((v) => v / cols.length * 0.5);
      this.demo.setTvLight(rgb);
    }
    this.applyWeights();
  }

  /* the raw shares the sliders hold, per record (CoverPaletteEditor: 0 to 2) */
  weightsOf(i) {
    if (!this.weights[i]) this.weights[i] = this.coverWeights(this.palOf(i).length);
    return this.weights[i];
  }
  coverWeights(n) { return n === 2 ? [1.1, 0.9] : [0.9, 0.64, 0.46]; }

  applyWeights() {
    const cols = this.palOf(this.trackI || 0), w = this.weightsOf(this.trackI || 0);
    const tot = w.reduce((a, b) => a + b, 0) || 1;
    const pct = w.map((x) => Math.round(x / tot * 100));
    if (this.demo) this.demo.setPalette(this.weightedPalette(cols, pct));
    this.paintPalRows(cols, w, pct);
  }

  /* turn the weights into lamp assignments: a heavier colour simply lands on
     more lamps, and neighbours are kept different */
  weightedPalette(cols, w) {
    const pool = [];
    cols.forEach((c, i) => {
      if (!(w[i] > 0)) return;
      const k = Math.max(1, Math.round(12 * w[i] / 100));
      for (let j = 0; j < k; j++) pool.push(c);
    });
    if (!pool.length) cols.forEach((c) => pool.push(c));
    const mix = [];
    let idx = 0;
    while (pool.length) { idx = (idx + 5) % pool.length; mix.push(pool.splice(idx, 1)[0]); }
    return mix;
  }

  paintPalRows(cols, w, pct) {
    for (let i = 0; i < 3; i++) {
      const on = i < cols.length;
      const strip = this.$('[data-strip="' + i + '"]');
      if (strip) { strip.hidden = !on; strip.style.flex = String(on ? Math.max(0.001, pct[i]) : 0); strip.style.background = on ? cols[i] : 'transparent'; }
      const row = this.$('[data-row="' + i + '"]');
      if (row) row.hidden = !on;
      if (!on) continue;
      const dot = this.$('[data-dot="' + i + '"]');
      if (dot) { dot.style.background = cols[i]; dot.classList.toggle('is-dark', needsLightInk(cols[i])); }
      const range = this.$('[data-w="' + i + '"]');
      if (range && document.activeElement !== range) { range.value = String(Math.round(w[i] * 100)); this.paintRange(range); }
      const p = this.$('[data-pct="' + i + '"]');
      if (p) p.textContent = pct[i] + '%';
    }
  }

  tint(hex, a) {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    if (!m) return 'rgba(228,120,50,' + a + ')';
    const v = parseInt(m[1], 16);
    return 'rgba(' + ((v >> 16) & 255) + ',' + ((v >> 8) & 255) + ',' + (v & 255) + ',' + a + ')';
  }

  paintTrackBars(i, cols) {
    const host = this.$('[data-bars="' + i + '"]');
    if (!host || !cols || !cols.length) return;
    let k = 0;
    for (const el of host.children) { el.style.background = cols[k % cols.length]; k++; }
  }

  selectCover(i) {
    const tr = this.tracks[i];
    if (!tr) return;
    this.coverI = i;
    if (this.refs.coverArt) {
      this.refs.coverArt.src = tr.cover;
      this.refs.coverArt.alt = tr.label + ' cover';
    }
    if (this.refs.covLabel) {
      const n = tr.sw.length;
      this.refs.covLabel.textContent = (n === 2 ? 'TWO' : 'THREE') + ' COLOURS, SAVED WITH THE ALBUM';
    }
    this.paintPalette(tr.sw);
  }

  paintPalette(cols) {
    for (let i = 0; i < 4; i++) {
      const el = this.$('[data-sw="' + i + '"]');
      if (!el) continue;
      el.hidden = i >= cols.length;
      el.style.background = cols[i % cols.length];
    }
    for (let i = 0; i < 5; i++) {
      const el = this.$('[data-lamp="' + i + '"]');
      if (!el) continue;
      const c = cols[i % cols.length];
      el.style.background = 'linear-gradient(180deg,' + c + ',rgba(0,0,0,0.35))';
      el.style.boxShadow = '0 0 26px ' + c + '55';
    }
  }

  startTilt() {
    const pad = this.refs.tiltPad, phone = this.refs.tiltPhone;
    if (!pad || !phone) return;
    const swatches = [this.refs.tiltA, this.refs.tiltB, this.refs.tiltC, this.refs.tiltD];
    const paint = (nx, ny) => {
      const hue = (210 + nx * 150 + 360) % 360;
      const light = 34 + (1 - ny) * 34;
      swatches.forEach((s, i) => {
        if (!s) return;
        s.style.background = 'hsl(' + ((hue + i * 14) % 360) + ',82%,' + light.toFixed(0) + '%)';
        s.style.boxShadow = '0 0 ' + (6 + (1 - ny) * 22).toFixed(0) + 'px hsla(' + ((hue + i * 14) % 360) + ',82%,' + light.toFixed(0) + '%,0.55)';
      });
      if (this.refs.tiltCopy) {
        this.refs.tiltCopy.textContent = nx < -0.25 ? 'Tilted left, the room walks its colour back down the wheel.'
          : nx > 0.25 ? 'Tilted right, the colour walks forward across the room.'
          : ny < 0.35 ? 'Lifted up, the whole room brightens with you.'
          : 'Flick it to flash every lamp, turn it to send a wave around the room, put it down and the show carries on by itself.';
      }
    };
    let dragging = false;
    const move = (ev) => {
      const r = pad.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width));
      const y = Math.max(0, Math.min(1, (ev.clientY - r.top) / r.height));
      phone.style.left = (x * 100).toFixed(1) + '%';
      phone.style.top = (y * 100).toFixed(1) + '%';
      phone.style.transform = 'rotate(' + ((x - 0.5) * 44).toFixed(1) + 'deg)';
      paint((x - 0.5) * 2, y);
    };
    this.on(pad, 'pointerdown', (ev) => { dragging = true; pad.setPointerCapture(ev.pointerId); pad.style.cursor = 'grabbing'; move(ev); });
    this.on(pad, 'pointermove', (ev) => { if (dragging) move(ev); });
    this.on(pad, 'pointerup', () => { dragging = false; pad.style.cursor = 'grab'; });
    paint(0, 0.5);
  }

  /* ---------- expanded feature demos ---------- */

  initExtras() {
    this.mpdTrack = 4; /* Memory Palace */
    this.mpdReset();
    this.grpReset();
    this.renderServer('navidrome');
    this.renderStream('spotify');
    this.renderAuto();
    this.renderSet('peak');
    this.renderDJ('morning');
    this.renderStat('poster');
    this.fit(this.refs.statViz);
    try { this.drawStat(); } catch (e) { console.error('[camusic] recap draw failed', e); }
    this.wireScatter();
    this.driveUpdate(this.driveV);
  }

  renderServer(key) {
    this.serverI = key;
    const s = SERVERS[key];
    if (!s || !this.refs.serverDetail) return;
    this.refs.serverDetail.innerHTML =
      '<span class="cmu-caps">' + s.caps + '</span><b>' + s.name + '</b><p>' + s.copy + '</p>';
    const tbody = this.refs.serverTable && this.refs.serverTable.querySelector('tbody');
    if (tbody) {
      for (const tr of tbody.children) tr.classList.toggle('is-on', tr.getAttribute('data-s') === key);
    }
  }

  renderStream(key) {
    this.streamI = key;
    const rows = this.refs.streamRows;
    if (!rows) return;
    for (const el of rows.querySelectorAll('.cmu-stream')) {
      el.classList.toggle('is-on', el.getAttribute('data-st') === key);
    }
    for (const el of rows.querySelectorAll('.cmu-stream-body')) {
      el.classList.toggle('is-open', el.getAttribute('data-stbody') === key);
      const d = STREAMS[el.getAttribute('data-stbody')];
      if (d && !el.innerHTML) el.innerHTML = d.html;
    }
  }

  renderAuto() {
    const body = this.refs.autoBody;
    if (!body) return;
    const a = this.auto;
    const shelf = (sh) => {
      const sw = a.round || sh.kind === 'people' ? 'border-radius:50%' : 'border-radius:4px';
      if (a.grid && sh.kind === 'grid') {
        return '<div class="cmu-auto__shelf is-grid"><b>' + sh.name + '</b><i></i><i></i><i></i><i></i><i></i><i></i></div>';
      }
      if (a.grid && sh.kind === 'people') {
        return '<div class="cmu-auto__shelf is-grid"><b>' + sh.name + '</b><i style="border-radius:50%"></i><i style="border-radius:50%"></i><i style="border-radius:50%"></i><i style="border-radius:50%"></i></div>';
      }
      return '<div class="cmu-auto__shelf"><b>' + sh.name + '</b><span class="cmu-auto__rowmeta"><span class="cmu-auto__thumb" style="' + sw + '"></span><em>browse rows with cover art</em></span></div>';
    };
    let html = '';
    if (a.shelves) {
      html += shelf(AUTO_SHELVES[0]);
      html += shelf(AUTO_SHELVES[1]);
      html += shelf(AUTO_SHELVES[2]);
    } else {
      html += '<div class="cmu-auto__shelf cmu-auto__shelf--off"><b>Shelves hidden</b><span>only the library browse tree reaches the car</span></div>';
    }
    html += shelf(AUTO_SHELVES[3]);
    const t = a.rwff ? '⏪ ⏮ ⏯ ⏭ ⏩' : '⏮ ⏯ ⏭';
    html += '<div class="cmu-auto__shelf cmu-auto__shelf--transport">' + t.split(' ').map((x) => '<span>' + x + '</span>').join('') + '</div>';
    body.innerHTML = html;
    const opts = this.root.querySelectorAll('.cmu-auto__opt');
    for (const o of opts) {
      o.classList.toggle('is-on', !!a[o.getAttribute('data-auto')]);
    }
  }

  driveUpdate(v) {
    this.driveV = v;
    const over = v > this.driveLimit + this.driveTol;
    const near = !over && v > this.driveLimit - 20;
    if (this.refs.driveSign) this.refs.driveSign.textContent = String(v);
    if (this.refs.driveYouVal) this.refs.driveYouVal.textContent = v + ' km/h';
    const your = this.refs.driveYour;
    if (your) your.classList.toggle('cmu-drive__speed--alert', over);
    if (this.refs.driveAlert) {
      this.refs.driveAlert.classList.toggle('is-on', over);
      const txt = this.refs.driveAlertTxt;
      if (txt) txt.textContent = over
        ? 'Speed limit ' + this.driveLimit + ', you\u2019re at ' + v
        : (near ? 'Approaching the limit, ' + this.driveLimit + ' ahead' : 'Limit ' + this.driveLimit + ', all good at ' + v);
      const small = this.refs.driveAlert.querySelector('small');
      if (small) small.textContent = over
        ? 'Victorian zone from the offline database, posted full width. A tone, a heads-up and a pulse arrive together.'
        : 'The alert disappears the moment you are back within speed boundaries. The zone database ships in the APK and currently covers Victoria, Australia.';
    }
    const ctl = this.refs.driveCtl;
    if (ctl) {
      for (const b of ctl.children) b.classList.toggle('is-on', b.getAttribute('data-v') === String(v));
    }
  }

  /* covers for the JS-rendered rows (Set Builder, DJ Radio): the LIB keys are
     bare asset names, so resolve them against the same asset root the config's
     track cover URLs use — CDN URLs in prod, assets/ in the local harness. */
  covUrl(t) {
    const c = this.tracks && this.tracks[0] && this.tracks[0].cover;
    if (!c) return 'assets/' + t.cov + '.webp';
    return c.replace(/[^/]*$/, '') + t.cov + '.webp';
  }

  renderSet(shapeKey) {
    this.shape = shapeKey;
    const shape = SET_SHAPES[shapeKey];
    const out = this.refs.setOut;
    if (!shape || !out) return;
    const used = [];
    const order = [];
    for (let i = 0; i < shape.curve.length; i++) {
      let best = -1, bestD = 1e9;
      for (let j = 0; j < SET_TRACKS.length; j++) {
        if (used.indexOf(j) >= 0) continue;
        const d = Math.abs(SET_TRACKS[j].en - shape.curve[i]);
        if (d < bestD) { bestD = d; best = j; }
      }
      used.push(best);
      order.push(SET_TRACKS[best]);
    }
    let html = '';
    for (let i = 0; i < order.length; i++) {
      const t = order[i];
      const open = i === 0 ? 'OPEN' : '+' + (i * 7 - 4) + ' MIN';
      html += '<div class="cmu-set__slot' + (i === 0 ? ' is-here' : '') + '"><i>' + open + '</i>' +
        '<img src="' + this.covUrl(t) + '" alt="" loading="lazy">' +
        '<span style="min-width:0"><b>' + t.t + '</b><span>' + t.a + ' · ' + t.al + ' · ' + t.bpm + ' BPM · ' + t.key + ' · energy ' + Math.round(t.en * 100) + '%</span></span>' +
        '<span style="font-size:10px;color:var(--text-faint)">slot ' + (i + 1) + '</span></div>';
    }
    out.innerHTML = html;
    if (this.refs.setCov) {
      this.refs.setCov.innerHTML = 'Built from twelve real records · scan coverage <b>12 / 12</b> stated up front · ordered by energy in this demo, with the app reporting what it had to work with first.';
    }
    const ctl = this.root.querySelector('.cmu-set__controls');
    if (ctl) {
      for (const b of ctl.children) b.classList.toggle('is-on', b.getAttribute('data-shape') === shapeKey);
    }
  }

  renderStat(key) {
    this.statI = key;
    const d = STAT_COPY[key];
    if (d && this.refs.statDetail) this.refs.statDetail.innerHTML = d;
    const row = this.refs.statDetail && this.refs.statDetail.previousElementSibling;
    if (row) {
      for (const b of row.children) b.classList.toggle('is-on', b.getAttribute('data-stat') === key);
    }
  }

  /* recap canvas: four views, drawn like the app renders them */
  drawStat(hoverI) {
    const c = this.refs.statViz;
    if (!c) return;
    const g = this.ctxOf(c);
    if (!g) return;
    const { ctx, w, h } = g;
    const mode = this.statI || 'poster';
    ctx.clearRect(0, 0, w, h);
    if (mode === 'poster') this.statPoster(ctx, w, h);
    else if (mode === 'clock') this.statClock(ctx, w, h);
    else if (mode === 'keys') this.statKeys(ctx, w, h);
    else this.drawScatter(hoverI);
  }

  drawScatter(hoverI) {
    const c = this.refs.statViz;
    if (!c) return;
    const g = this.ctxOf(c);
    if (!g) return;
    const { ctx, w, h } = g;
    const px = 34, py = 18, pw = w - px - 12, ph = h - py - 26;
    ctx.clearRect(0, 0, w, h);
    ctx.strokeStyle = 'rgba(255,255,255,0.09)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const yy = py + ph * i / 4;
      ctx.beginPath(); ctx.moveTo(px, yy); ctx.lineTo(px + pw, yy); ctx.stroke();
      ctx.fillStyle = 'rgba(255,255,255,0.28)';
      ctx.font = '9px Inter, sans-serif';
      ctx.fillText(String(160 - i * 30), 6, yy + 3);
    }
    for (let i = 0; i <= 4; i++) {
      const xx = px + pw * i / 4;
      ctx.fillText(String(60 + i * 30), xx - 8, h - 8);
    }
    ctx.fillText('BPM →', w - 46, py - 6);
    this._scatterPts = [];
    for (let i = 0; i < SET_TRACKS.length; i++) {
      const t = SET_TRACKS[i];
      const xx = px + pw * (t.bpm - 60) / 100;
      const yy = py + ph * (1 - t.en);
      this._scatterPts.push({ x: xx, y: yy, t: t });
      const col = t.key.indexOf('min') >= 0 ? '#7FB2E8' : '#E4A667';
      const r = hoverI === i ? 6.5 : 4.5;
      ctx.beginPath();
      ctx.arc(xx, yy, r, 0, Math.PI * 2);
      ctx.fillStyle = col;
      ctx.globalAlpha = hoverI === i || hoverI == null ? 0.95 : 0.4;
      ctx.fill();
      ctx.globalAlpha = 1;
      if (hoverI === i) {
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        ctx.font = '600 10.5px Inter, sans-serif';
        const label = t.t + ' · ' + t.bpm + ' BPM · ' + t.key;
        const tx = Math.min(xx + 9, w - 170);
        ctx.fillText(label, tx, yy - 9);
      }
    }
  }

  /* the shareable poster: window label, top artist strip, headline numbers */
  statPoster(ctx, w, h) {
    const grad = ctx.createLinearGradient(0, 0, w, h);
    grad.addColorStop(0, 'rgba(228,166,103,0.16)');
    grad.addColorStop(1, 'rgba(127,178,232,0.10)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = 'rgba(228,166,103,0.95)';
    ctx.font = '600 11px Inter, sans-serif';
    ctx.fillText('YOUR LISTENING RECAP · MEASURED ON THIS PHONE', 22, 34);
    ctx.fillStyle = '#F0BE8A';
    ctx.font = '700 30px Barlow, Inter, sans-serif';
    ctx.fillText('1,284 plays', 22, 74);
    ctx.fillStyle = 'rgba(255,255,255,0.62)';
    ctx.font = '12px Inter, sans-serif';
    ctx.fillText('across 12 records · 61% lossless · 34 artists', 22, 94);
    /* top artists strip: covers scaled by play share */
    const shares = [0.22, 0.17, 0.14, 0.11, 0.09, 0.08];
    const cols = ['#E4A667', '#7FB2E8', '#A78BFA', '#35C2C2', '#F5A623', '#E4407A'];
    let x = 22;
    const y = h - 74, bh = 44;
    for (let i = 0; i < 6; i++) {
      const t = LIB[i];
      const bw = Math.max(14, Math.round(shares[i] * w * 0.86));
      roundRect(ctx, 22 + (i * 62), y, 54, bh, 7);
      ctx.fillStyle = 'rgba(255,255,255,0.09)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.2)';
      ctx.stroke();
      ctx.fillStyle = ['29.5%', '21%', '16.5%', '13%', '11%', '9%'][i] && i === 0 ? '#E4A667' : 'rgba(255,255,255,0.5)';
      ctx.font = '9px Inter, sans-serif';
      ctx.fillText(['Coldplay', 'Post Malone', 'Mildlife', 'Lauv', 'Adele', 'Kupla'][i].slice(0, 9), 26 + i * 62, y + bh - 8);
    }
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.font = '10.5px Inter, sans-serif';
    ctx.fillText('artist share of plays · the poster carries this strip at the foot', 22, h - 14);
  }

  statClock(ctx, w, h) {
    const cx = w / 2, cy = h / 2, R = Math.min(w, h) / 2 - 26;
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.arc(cx, cy, R, 0, 6.2832); ctx.stroke();
    ctx.beginPath(); ctx.arc(cx, cy, R * 0.55, 0, 6.2832); ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.font = '9px Inter, sans-serif';
    for (let hh = 0; hh < 24; hh += 6) {
      const a = hh / 24 * Math.PI * 2 - Math.PI / 2;
      ctx.fillText(String(hh).padStart(2, '0'), cx + Math.cos(a) * (R + 12) - 7, cy + Math.sin(a) * (R + 12) + 3);
    }
    /* play density by hour: morning brief, work focus, after-dinner */
    const density = [0,0,0,0,0,0,0.12,0.3,0.5,0.42,0.35,0.3,0.28,0.34,0.3,0.24,0.3,0.42,0.6,0.72,0.66,0.5,0.3,0.1];
    for (let hh = 0; hh < 24; hh++) {
      const a0 = (hh / 24) * Math.PI * 2 - Math.PI / 2;
      const len = 8 + density[hh] * (R - 18);
      ctx.strokeStyle = hh >= 18 || hh < 1 ? '#E4A667' : '#7FB2E8';
      ctx.lineWidth = 5;
      ctx.globalAlpha = 0.28 + density[hh] * 0.6;
      ctx.beginPath();
      ctx.arc(cx, cy, 14 + len / 2, a0 + 0.02, a0 + Math.PI / 12 - 0.02);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.font = '600 12px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('when the plays land', cx, cy + 4);
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.font = '9px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('blue = day · amber = evening', 12, h - 12);
  }

  statKeys(ctx, w, h) {
    const keys = {};
    for (const t of LIB) keys[t.key] = (keys[t.key] || 0) + 1;
    const rows = Object.keys(keys).sort((x, y) => keys[y] - keys[x]).slice(0, 8);
    const bw = (w - 90) / Math.max(...rows.map((k) => keys[k]));
    const label = { morning: 'Morning brief', focus: 'Focus brief', workout: 'Workout brief', party: 'Party brief', sundown: 'Sundown brief', sleep: 'Before-sleep brief' };
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.font = '600 11px Inter, sans-serif';
    ctx.fillText('KEYS THE SCAN DETECTED', 22, 30);
    rows.forEach((k, i) => {
      const y = 52 + i * ((h - 80) / rows.length);
      ctx.fillStyle = k.indexOf('min') >= 0 ? '#7FB2E8' : '#E4A667';
      ctx.globalAlpha = 0.85;
      roundRect(ctx, 22, y, keys[k] * (w - 120) / 3, 16, 5);
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.font = '10.5px Inter, sans-serif';
      ctx.fillText(k, 30, y + 12);
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.fillText(keys[k] + ' track' + (keys[k] > 1 ? 's' : ''), 22 + (w - 120) / 3 + 14, y + 12);
    });
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.font = '10px Inter, sans-serif';
    ctx.fillText('BPM sweet spot: 78 to 128 · the same detection feeds DJ Radio\'s key matching', 22, h - 18);
  }

  wireScatter() {
    const c = this.refs.statViz;
    if (!c) return;
    const move = (ev) => {
      if (this.statI !== 'scatter' || !this._scatterPts) return;
      const r = c.getBoundingClientRect();
      const mx = ev.clientX - r.left, my = ev.clientY - r.top;
      let hi = null, best = 16;
      for (let i = 0; i < this._scatterPts.length; i++) {
        const p = this._scatterPts[i];
        const d = Math.hypot(p.x - mx, p.y - my);
        if (d < best) { best = d; hi = i; }
      }
      this.drawStat(hi);
    };
    this.on(c, 'pointermove', move);
    this.on(c, 'pointerleave', () => { if (this.statI === 'scatter') this.drawStat(null); });
  }

  /* ---------- MPD: phone commands, daemon answers ---------- */

  mpdReset() {
    this.mpd = { playing: false, t: 0, dur: 372, brTimer: 0 };
    this.mpdPaint();
  }

  mpdPaint() {
    const m = this.mpd;
    if (this.refs.mpdToggle) {
      this.refs.mpdToggle.innerHTML = m.playing ? '&#9208;' : '&#9654;';
    }
  }

  mpdFlash(cmd) {
    const wire = this.refs.mpdWire && this.refs.mpdWire.parentElement;
    if (!wire) return;
    const label = { play: 'play', pause: 'pause', prev: 'previous', next: 'next' }[cmd] || cmd;
    if (this.refs.mpdWire) {
      this.refs.mpdWire.textContent = label;
      this.refs.mpdWire.classList.add('is-on');
    }
    wire.classList.remove('is-sending');
    void wire.offsetWidth; /* restart the travel animation */
    wire.classList.add('is-sending');
    clearTimeout(this._mpdWireT);
    this._mpdWireT = setTimeout(() => {
      if (this.refs.mpdWire) this.refs.mpdWire.classList.remove('is-on');
      wire.classList.remove('is-sending');
    }, 900);
    const waves = this.refs.mpdWaves;
    if (waves) waves.classList.toggle('is-on', this.mpd.playing);
    this.mpdBrPaint();
    /* a live readout needs to live-update: tick the decode bitrate while playing */
    if (this._mpdBrT) { clearInterval(this._mpdBrT); this._mpdBrT = 0; }
    if (this.mpd.playing) {
      this._mpdBrT = setInterval(() => {
        if (!this.mpd.playing) { if (this._mpdBrT) { clearInterval(this._mpdBrT); this._mpdBrT = 0; } this.mpdBrPaint(); return; }
        this.mpd.t += 0.5;
        this.mpdBrPaint();
      }, 500);
    }
  }

  mpdBrPaint() {
    if (!this.refs.mpdBr) return;
    if (!this.mpd.playing) { this.refs.mpdBr.textContent = 'idle'; return; }
    const wob = 2400 + Math.floor(Math.sin(this.mpd.t / 7) * 420 + Math.sin(this.mpd.t / 2.3) * 160);
    this.refs.mpdBr.textContent = Math.round(wob / 100) / 10 + ' Mb/s';
  }

  mpdCmd(el) {
    const cmd = el.getAttribute('data-cmd');
    if (cmd === 'play') {
      this.mpd.playing = !this.mpd.playing;
    } else if (cmd === 'next') {
      this.mpdTrack = ((this.mpdTrack || 0) + 1) % LIB.length;
    } else if (cmd === 'prev') {
      this.mpdTrack = (this.mpdTrack - 1 + LIB.length) % LIB.length;
    }
    if (cmd !== 'play') {
      const t = LIB[this.mpdTrack == null ? 4 : this.mpdTrack];
      if (this.refs.mpdTrack) this.refs.mpdTrack.textContent = t.t;
      if (this.refs.mpdSub) this.refs.mpdSub.textContent = t.a + ' · ' + t.al;
      if (this.refs.mpdCover) {
        const base = this.refs.mpdCover.src.replace(/[^/]*$/, '');
        this.refs.mpdCover.src = base + t.cov + '.webp';
      }
    }
    this.mpdPaint();
    this.mpdFlash(cmd === 'play' ? (this.mpd.playing ? 'play' : 'pause') : cmd);
  }

  /* ---------- Sendspin group ---------- */

  grpReset() {
    this.grp = { playing: false, t: 0, dur: 364, rooms: {} };
    this.grpPaint(true);
  }
  grpToggle() {
    this.grp.playing = !this.grp.playing;
    if (this.refs.grpBtn) {
      this.refs.grpBtn.innerHTML = this.grp.playing ? '&#9208; Stop' : '&#9654; Start listening';
    }
    if (this.grp.playing) {
      for (const k of Object.keys(this.grp.rooms)) this.grp.rooms[k] = 0;
    }
    this.grpPaint(true);
    this.grpTick();
  }

  grpPaint(full) {
    const g = this.grp;
    if (this.refs.grpBtn && full !== 'tick') {
      this.refs.grpBtn.innerHTML = g.playing ? '&#9208; Stop' : '&#9654; Start listening';
    }
    const host = this.refs.grpNodes;
    if (!host) return;
    for (const el of host.querySelectorAll('.cmu-grp__node')) {
      const k = el.getAttribute('data-room');
      const inGroup = k === 'phone' || (k in g.rooms && g.rooms[k] != null);
      el.classList.toggle('is-in', inGroup);
      el.classList.toggle('is-joining', g.playing && g.rooms[k] === 'joining');
      el.classList.toggle('is-playing', inGroup && g.playing);
      const offEl = el.querySelector('.cmu-grp__off');
      if (offEl) {
        if (!inGroup) offEl.textContent = 'tap to add';
        else if (!g.playing) offEl.textContent = 'in the group · idle';
        else if (g.rooms[k] > 0) offEl.textContent = 'locking clock …';
        else offEl.textContent = k === 'phone' ? 'master clock · +0.0 ms' : 'locked · +0.0 ms';
      }
    }
  }

  grpTick() {
    if (this._grpRaf) return;
    let last = performance.now();
    const step = (now) => {
      const g = this.grp;
      if (!g.playing) { this._grpRaf = 0; return; }
      const dt = (now - last) / 1000;
      last = now;
      g.t += dt;
      if (this.refs.grpProg) this.refs.grpProg.style.width = ((g.t % 364) / 364 * 100).toFixed(1) + '%';
      /* joining rooms count their ms-remaining down to zero, then lock */
      for (const k of Object.keys(g.rooms)) {
        const v = g.rooms[k];
        if (v == null || v <= 0) { g.rooms[k] = 0; continue; }
        const next = Math.max(0, v - dt * 2200);
        g.rooms[k] = next;
        const offEl = this.root.querySelector('[data-off="' + k + '"]');
        if (offEl) offEl.textContent = next > 0 ? 'locking · +' + (next / 1000).toFixed(1) + ' s' : 'locked · +0.0 ms';
      }
      this.grpPaint('tick');
      this._grpRaf = requestAnimationFrame(step);
    };
    this._grpRaf = requestAnimationFrame(step);
  }

  /* ---------- DJ Radio ---------- */

  renderDJ(brief) {
    this.brief = brief;
    const briefs = {
      morning: { en: [0.35, 0.75], bpm: [90, 118], why: 'gentle openers at a mid tempo' },
      focus: { en: [0.2, 0.5], bpm: [70, 95], why: 'low energy and steady' },
      workout: { en: [0.7, 1.0], bpm: [115, 140], why: 'high energy, driving tempo' },
      party: { en: [0.75, 1.0], bpm: [110, 140], why: 'the loudest, fastest corner of the library' },
      sundown: { en: [0.35, 0.65], bpm: [80, 105], why: 'warm, unhurried, room-friendly' },
      sleep: { en: [0.1, 0.35], bpm: [60, 85], why: 'quietest material, slowest tempos' }
    };
    const b = briefs[brief] || briefs.morning;
    const inBand = LIB.filter((t) => t.en >= b.en[0] && t.en <= b.en[1] && t.bpm >= b.bpm[0] && t.bpm <= b.bpm[1]);
    const pool = (inBand.length >= 4 ? inBand : LIB.slice().sort((x, y) => Math.abs(x.en - (b.en[0] + b.en[1]) / 2)).slice(0, 4)).slice();
    /* the opener: the track closest to the middle of the brief */
    const mid = (b.en[0] + b.en[1]) / 2;
    const opener = inBand.slice().sort((x, y) => Math.abs(x.en - mid) - Math.abs(y.en - mid))[0] || pool[0];
    const rest = LIB.filter((t) => t !== opener);
    /* next three chosen on genre+energy+tempo together: nearest energy, key a fifth apart or same */
    const picks = [opener];
    let cur = opener;
    for (let i = 0; i < 3; i++) {
      rest.sort((x, y) => (Math.abs(x.en - cur.en) + Math.abs(x.bpm - cur.bpm) / 240) - (Math.abs(y.en - cur.en) + Math.abs(y.bpm - cur.bpm) / 240));
      picks.push(rest.shift());
      cur = picks[picks.length - 1];
    }
    const whyTags = { morning: 'Morning brief', focus: 'Focus brief', workout: 'Workout brief', party: 'Party brief', sundown: 'Sundown brief', sleep: 'Before-sleep brief' };
    let html = '';
    picks.forEach((t, i) => {
      const sameKey = t.key === cur.key;
      const bpmD = Math.abs(t.bpm - cur.bpm);
      const why = i === 0 ? whyTags[brief] || 'Brief' : (bpmD <= 8 ? 'energy matched · ' : 'energy close · ') + (bpmD < 8 ? 'tempo ' + bpmD + ' away' : 'tempo folded ' + (bpmD > 30 ? 'double time' : 'half time'));
      html += '<div class="cmu-dj__row' + (i === 0 ? ' cmu-dj__out-first' : '') + '">' +
        '<img src="' + this.covUrl(t) + '" alt="" loading="lazy">' +
        '<span style="min-width:0"><b>' + t.t + '</b><span>' + t.a + ' · ' + t.bpm + ' BPM · ' + t.key + '</span></span>' +
        '<span class="cmu-dj__why">' + (i === 0 ? 'opening' : why) + '</span></div>';
    });
    html += '<p class="cmu-dj__note">' + b.why + ', with overlap starting on a downbeat, sized in whole bars for seamless continuity.</p>';
    if (this.refs.djOut) this.refs.djOut.innerHTML = html;
    if (this.refs.djBriefs) {
      for (const chip of this.refs.djBriefs.children) chip.classList.toggle('is-on', chip.getAttribute('data-brief') === brief);
    }
  }
}

/* rounded-rect path helper for the recap canvases */
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/* handlers, keyed by the data-act / data-act-input attribute in the markup */
CAMusicPage.ACTIONS = {
  togglePlay() {
    if (!this.demo) return;
    if (this.view === 'ambience') return;
    this.demo.toggle();
  },

  /* the Light Sync switch: off hands the lamps back to a steady scene while
     the music carries on, exactly as the app does */
  toggleSync() {
    this.syncOn = !this.syncOn;
    const t = this.refs.toggle;
    if (t) { t.classList.toggle('is-on', this.syncOn); t.setAttribute('aria-checked', String(this.syncOn)); }
    if (this.demo) this.demo.setSync(this.syncOn);
    this.renderStatus();
  },

  toggleMute() {
    this.muted = !this.muted;
    const b = this.refs.mute;
    if (b) {
      b.classList.toggle('is-muted', this.muted);
      b.setAttribute('aria-pressed', String(this.muted));
      b.setAttribute('aria-label', this.muted ? 'Unmute the music' : 'Mute the music');
    }
    if (this.demo) this.demo.setMuted(this.muted);
  },

  prevTrack() { if (this.demo) this.selectTrack((this.trackI || 0) - 1); },
  nextTrack() { if (this.demo) this.selectTrack((this.trackI || 0) + 1); },

  pickTrack(el) {
    if (!this.demo) return;
    this.selectTrack(parseInt(el.getAttribute('data-i'), 10) || 0);
  },

  pickIntensity(el) {
    this.setIntensity(el.getAttribute('data-int'));
  },

  setWeight(el) {
    const k = parseInt(el.getAttribute('data-w'), 10) || 0;
    const w = this.weightsOf(this.trackI || 0);
    w[k] = Math.max(0, (parseInt(el.value, 10) || 0) / 100);
    this.paintRange(el);
    this.markGroup(this.refs.palMode, 'data-pal', 'custom');
    this.applyWeights();
  },

  setModeAmount(el) {
    const v = parseInt(el.value, 10) / 100;
    if (this.modeAmt[this.mode] === undefined) return;
    this.modeAmt[this.mode] = v;
    if (this.refs.modeAmtVal) this.refs.modeAmtVal.textContent = Math.round(v * 100) + '%';
    if (this.modePlayer) this.modePlayer.setAmount(this.mode, v);
  },

  pickPalMode(el) {
    const m = el.getAttribute('data-pal');
    const i = this.trackI || 0, n = this.palOf(i).length;
    this.weights[i] = m === 'even' ? new Array(n).fill(1) : this.coverWeights(n);
    this.markGroup(this.refs.palMode, 'data-pal', m);
    this.applyWeights();
  },

  pickAmb(el) {
    const key = el.getAttribute('data-amb');
    this.ambKey = key;
    const host = this.refs.ambChips;
    if (host) for (const b of host.children) {
      const on = b.getAttribute('data-amb') === key;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', String(on));
    }
    const a = AMB[key];
    if (this.refs.ambTitle) this.refs.ambTitle.textContent = a.title;
    if (this.ambRoom) this.ambRoom.set(key);
    if (this.ambSound) this.ambSound.set(key);
    this.ambBleed(key);
    this.renderCaption();
  },

  /* Music or Ambience: the phone changes screen, the room changes show, and
     the phone's size never changes, so nothing on the page moves */
  pickView(el) {
    const v = el.getAttribute('data-view');
    if (v === this.view) return;
    this.view = v;
    const sw = this.refs.viewSwitch;
    if (sw) for (const b of sw.children) {
      const on = b.getAttribute('data-view') === v;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-selected', String(on));
    }
    const music = v === 'music';
    if (this.refs.musicPanel) this.refs.musicPanel.hidden = !music;
    if (this.refs.ambPanel) this.refs.ambPanel.hidden = music;
    if (this.refs.libRow) this.refs.libRow.classList.toggle('is-dim', !music);
    if (this.refs.camDemo) this.refs.camDemo.classList.toggle('is-amb', !music);
    if (this.demo) this.demo.setActive(music);
    if (this.ambRoom) this.ambRoom.setActive(!music);
    if (music && this.ambSound && this.ambSound.playing) CAMusicPage.ACTIONS.toggleAmbSound.call(this);
    this.setAccent(music ? this.albumAccent : this.ambAccent);
    this.renderCaption();
    this.renderPlay();
  },

  setAmbSound(el) {
    const want = el.getAttribute('data-snd') === 'on';
    if (want !== !!(this.ambSound && this.ambSound.playing)) CAMusicPage.ACTIONS.toggleAmbSound.call(this);
  },

  toggleAmbSound() {
    this.importSpec(this.cfg.modules.sfx).then((mod) => {
      if (!this.ambSound) {
        mod.setSfxUrls(this.cfg.sfx || {});
        this.ambSound = mod.createAmbienceSound();
        this.ambSound.setVolume(this.ambVol);
      }
      const key = this.ambKey || 'fireplace';
      if (this.ambSound.playing) {
        this.ambSound.stop();
        this.markAmbSound(false);
        if (this.ambRoom) this.ambRoom.setLevelSource(null);
      } else {
        this.ambSound.set(key);
        this.ambSound.start();
        this.ambSound.set(key);
        this.markAmbSound(true);
        if (this.ambRoom) this.ambRoom.setLevelSource(() => this.ambSound.getLevel());
      }
    });
  },

  setAmbVol(el) {
    const v = (parseInt(el.value, 10) || 0) / 100;
    this.ambVol = v;
    this.paintRange(el);
    if (this.refs.ambVolVal) this.refs.ambVolVal.textContent = Math.round(v * 100) + '%';
    if (this.ambSound) this.ambSound.setVolume(v);
  },

  toggleStage(el) {
    const c = el.getAttribute('data-c');
    const i = this.off.indexOf(c);
    if (i >= 0) this.off.splice(i, 1); else this.off.push(c);
    this.applyChain(null);
  },

  pickRung(el) {
    const r = parseInt(el.getAttribute('data-r'), 10) || 0;
    this.rung = r;
    this.off = RUNG[r].off.slice();
    this.applyChain(r);
  },

  toggleModeAudio() {
    this.importSpec(this.cfg.modules.sfx).then((mod) => {
      if (!this.modePlayer) {
        this.modePlayer = mod.createModePlayer(this.cfg.modeTrackUrl);
        this.modePlayer.setMode(this.mode);
      }
      const on = this.modePlayer.toggle();
      if (this.refs.modePlay) {
        this.refs.modePlay.textContent = on ? '■ Stop' : '▶ Hear it';
        this.refs.modePlay.classList.toggle('is-on', on);
      }
      if (this.refs.modeHint) this.refs.modeHint.textContent = on ? 'That waveform is the real processed signal.' : 'Real audio, processed in the browser, switch modes while it plays.';
    });
  },

  pickCover(el) {
    const i = parseInt(el.getAttribute('data-cov'), 10) || 0;
    this.markGroup(this.refs.covPick, 'data-cov', String(i));
    this.selectCover(i);
  },

  pickJoin(el) {
    const j = el.getAttribute('data-j');
    this.markGroup(this.refs.joins, 'data-j', j);
    this.joinMode = j;
    if (this.refs.joinCopy) this.refs.joinCopy.textContent = JOIN[j].copy;
    if (this.reduced) this.drawJoins(0);
  },

  slamFlash() {
    this.flashSlam = 1.15;
    if (this.reduced) this.drawFlash(0);
  },

  pickSound(el) {
    const m = el.getAttribute('data-m');
    this.markGroup(this.refs.modes, 'data-m', m);
    this.mode = m;
    if (this.refs.modeCopy) this.refs.modeCopy.textContent = MODE_COPY[m];
    if (this.modePlayer) this.modePlayer.setMode(m);
    this.syncModeAmount();
    if (this.reduced) this.drawModes(0);
  },

  /* ---------- feature popup ---------- */

  openFeat(el) {
    const key = el.getAttribute('data-f');
    const d = FEAT_COPY[key];
    if (!d) return;
    this._featReturn = el;
    if (this.refs.featModalTitle) this.refs.featModalTitle.textContent = d.t;
    if (this.refs.featModalBody) {
      this.refs.featModalBody.innerHTML = d.p.map((x) => '<p>' + x + '</p>').join('');
    }
    if (this.refs.featModalIcon) {
      const src = el.querySelector('.cmu-feat__i svg');
      if (src) {
        this.refs.featModalIcon.innerHTML = src.outerHTML;
        this.refs.featModalIcon.hidden = false;
      } else {
        this.refs.featModalIcon.hidden = true;
      }
    }
    if (this.refs.featModal) {
      this.refs.featModal.classList.add('is-open');
      const closeBtn = this.refs.featModal.querySelector('.cmu-modal__close');
      if (closeBtn) closeBtn.focus();
    }
  },

  closeFeat() {
    if (!this.refs.featModal) return;
    this.refs.featModal.classList.remove('is-open');
    if (this._featReturn) { try { this._featReturn.focus(); } catch (e) {} this._featReturn = null; }
  },

  /* ---------- expanded features ---------- */

  pickServer(el) {
    this.renderServer(el.getAttribute('data-s'));
  },

  pickStream(el) {
    this.renderStream(el.getAttribute('data-st'));
  },

  toggleAuto(el) {
    const k = el.getAttribute('data-auto');
    this.auto[k] = !this.auto[k];
    this.renderAuto();
  },

  driveThrottle(el) {
    this.driveUpdate(parseInt(el.getAttribute('data-v'), 10) || 0);
  },

  pickShape(el) {
    this.renderSet(el.getAttribute('data-shape'));
  },

  pickStat(el) {
    this.renderStat(el.getAttribute('data-stat'));
    try { this.drawStat(); } catch (e) { /* keep the previous view */ }
    if (this.refs.statCap) {
      const caps = {
        poster: 'The shareable poster, as the app draws it: the window you pick, measured on your phone.',
        clock: 'Every play plotted by hour: morning brief, work focus, after-dinner records.',
        scatter: 'Each dot is a record the offline scan measured: tempo across, energy up, colour by key. Hover a dot.',
        keys: 'Dominant keys across the year, the same detection DJ Radio leans on for harmonic joins.'
      };
      this.refs.statCap.textContent = caps[this.statI] || '';
    }
  },

  grpToggle() {
    if (!this.grp) return;
    this.grp.playing = !this.grp.playing;
    if (this.grp.playing) {
      /* already-joined rooms lock instantly; the group timer starts */
      for (const k of Object.keys(this.grp.rooms)) this.grp.rooms[k] = 'locked';
    }
    this.grpPaint(true);
    this.grpTick();
  },

  djReshuffle() {
    const all = ['morning', 'focus', 'workout', 'party', 'sundown', 'sleep'];
    const next = all[Math.floor(Math.random() * all.length)];
    this.renderDJ(next);
  },

  pickBrief(el) {
    this.renderDJ(el.getAttribute('data-brief'));
  },

  mpdCmd(el) {
    /* the instance method of the same name does the work */
    CAMusicPage.prototype.mpdCmd.call(this, el);
  },

  /* ---------- feature popup ---------- */

  pickRoom(el) {
    const k = el.getAttribute('data-room');
    if (k === 'phone' || !this.grp) return;
    if (this.grp.rooms[k] != null) { delete this.grp.rooms[k]; }
    else { this.grp.rooms[k] = 4200; this.grpTick(); }
    this.grpPaint(true);
  },

  ringAnnounce() {
    const a = this.refs.announce;
    if (!a) return;
    const on = a.classList.toggle('is-on');
    a.innerHTML = on
      ? '<b>Front door</b>, announcement ducking the music, back in 4 seconds …'
      : '<b>Front door</b>, announcement finished, the album came straight back up.';
    if (this._annTimer) clearTimeout(this._annTimer);
    if (on) {
      this._annTimer = setTimeout(() => {
        if (!this.refs.announce) return;
        this.refs.announce.classList.remove('is-on');
        this.refs.announce.innerHTML = '<b>Front door</b>, announcement finished, the album came straight back up.';
      }, 4000);
    }
  },

  /* the old mpd player actions are gone — the visual command flow replaced them */
};

/* ---------- boot ---------- */

const INSTANCES = new WeakMap();

function boot(root) {
  if (!root || INSTANCES.has(root)) return;
  const raw = root.querySelector('[data-camusic-config]');
  if (!raw) return;
  let cfg;
  try { cfg = JSON.parse(raw.textContent); }
  catch (e) { console.error('[camusic] bad config', e); return; }
  const page = new CAMusicPage(root, cfg);
  INSTANCES.set(root, page);
  page.mount();
}

function bootAll(scope) {
  (scope || document).querySelectorAll('[data-camusic]').forEach(boot);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bootAll());
} else {
  bootAll();
}

/* the theme editor re-renders a section in place */
document.addEventListener('shopify:section:load', (e) => bootAll(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('[data-camusic]').forEach((root) => {
    const inst = INSTANCES.get(root);
    if (inst) { inst.destroy(); INSTANCES.delete(root); }
  });
});
